import { createSequence } from '@olivierpascal/helpers';
import { Button } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { Table } from '~/components/molecules/Table';
import {
  LegalDocumentListItem,
  LegalDocumentListItemSkeleton,
} from '~/components/organisms/LegalDocumentListItem';
import { useLegalDocumentsQuery } from '~/lib/api/modules/LegalDocuments/hooks/useLegalDocumentsQuery';

const GRID_TEMPLATE_COLUMNS = 'repeat(3, 1fr) min-content';
const DEFAULT_SKELETON_COUNT = 3;

export type ILegalDocumentListProps = IContainerProps & {
  appId: string;
};

export const LegalDocumentList: React.FC<ILegalDocumentListProps> = (props) => {
  const { sx, appId } = props;
  const { t } = useTranslation();
  const legalDocumentsQuery = useLegalDocumentsQuery({ appId });

  const legalDocuments = legalDocumentsQuery.data;
  const isEmpty =
    !legalDocumentsQuery.isLoading && !legalDocuments?.items.length;

  return (
    <ContentBlock sx={sx}>
      {legalDocumentsQuery.isError ? (
        <ContentBlock.Body first>
          <ErrorMessage error={legalDocumentsQuery.error} />
        </ContentBlock.Body>
      ) : isEmpty ? (
        <ContentBlock.Empty title={t('LegalDocumentList.empty.title')}>
          <Button href={`/apps/${appId}/settings/legalDocuments/create`}>
            {t('LegalDocumentList.empty.action-label')}
          </Button>
        </ContentBlock.Empty>
      ) : (
        <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
          <Table.Header>{t('common.name')}</Table.Header>
          <Table.Header>{t('common.required')}</Table.Header>
          <Table.Header>{t('common.type')}</Table.Header>
          <Table.Header />

          {legalDocumentsQuery.isLoading
            ? createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
                <LegalDocumentListItemSkeleton key={index} />
              ))
            : legalDocuments?.items.map((legalDocument) => (
                <LegalDocumentListItem
                  key={legalDocument._id}
                  legalDocument={legalDocument}
                />
              ))}
        </Table>
      )}
    </ContentBlock>
  );
};
